import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Truck, Package, MapPin, Clock, CheckCircle, AlertCircle, 
  Phone, User, ArrowLeft, RefreshCw, Loader2 
} from "lucide-react";
import api from "@/utils/api";

const STATUS_CONFIG = {
  pending: {
    color: "bg-yellow-100 text-yellow-800",
    icon: Clock,
    label: "Pending",
    description: "Waiting for driver assignment"
  },
  assigned: {
    color: "bg-blue-100 text-blue-800",
    icon: User,
    label: "Driver Assigned",
    description: "Driver is heading to pickup location"
  },
  in_transit: {
    color: "bg-purple-100 text-purple-800",
    icon: Truck,
    label: "In Transit",
    description: "Package is on the way to you"
  },
  delivered: {
    color: "bg-emerald-100 text-emerald-800",
    icon: CheckCircle,
    label: "Delivered",
    description: "Package has been delivered"
  },
  failed: {
    color: "bg-red-100 text-red-800",
    icon: AlertCircle,
    label: "Failed",
    description: "Delivery attempt failed"
  },
  cancelled: {
    color: "bg-gray-100 text-gray-800",
    icon: AlertCircle,
    label: "Cancelled",
    description: "Delivery was cancelled"
  }
};

const DeliveryTrackingPage = () => {
  const { deliveryId } = useParams();
  const navigate = useNavigate();
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDeliveryStatus = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);
    
    try {
      const res = await api.get(`/delivery/status/${deliveryId}`);
      setDelivery(res.data);
    } catch (error) {
      toast.error("Failed to load delivery status");
      if (error.response?.status === 404) {
        navigate("/");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDeliveryStatus();
    
    // Poll for updates every 30 seconds
    const interval = setInterval(() => {
      fetchDeliveryStatus(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [deliveryId]);

  if (loading) {
    return (
      <div className="min-h-screen pt-52 pb-16 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading delivery status...</p>
        </div>
      </div>
    );
  }

  if (!delivery) {
    return (
      <div className="min-h-screen pt-52 pb-16">
        <div className="container mx-auto px-4 text-center">
          <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Delivery Not Found</h1>
          <p className="text-muted-foreground mb-6">
            The delivery you are looking for does not exist or you do not have access to it.
          </p>
          <Button onClick={() => navigate("/")}>Go Home</Button>
        </div>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[delivery.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen pt-52 pb-16" data-testid="delivery-tracking-page">
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-outfit">Track Delivery</h1>
            <p className="text-muted-foreground text-sm">ID: {delivery.delivery_id}</p>
          </div>
          <Button 
            variant="outline" 
            size="icon" 
            className="ml-auto"
            onClick={() => fetchDeliveryStatus(false)}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Status Card */}
        <Card className="p-6 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className={`p-4 rounded-full ${statusConfig.color}`}>
              <StatusIcon className="h-8 w-8" />
            </div>
            <div>
              <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
              <p className="text-lg font-semibold mt-1">{statusConfig.description}</p>
            </div>
          </div>

          {/* Progress Timeline */}
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
            {delivery.status_history?.map((event, index) => (
              <div key={index} className="relative flex items-start gap-4 pb-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                  index === 0 ? 'bg-emerald-500 text-white' : 'bg-gray-200'
                }`}>
                  {index === 0 ? <CheckCircle className="h-4 w-4" /> : <div className="w-2 h-2 bg-gray-400 rounded-full" />}
                </div>
                <div>
                  <p className="font-medium capitalize">{event.status.replace('_', ' ')}</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(event.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Item Info */}
        <Card className="p-4 mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Package className="h-4 w-4" />
            Item Details
          </h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="font-medium">{delivery.listing_title}</p>
              <p className="text-sm text-muted-foreground">
                Delivery Cost: <span className="font-semibold text-emerald-600">${delivery.cost?.toFixed(2)}</span>
              </p>
            </div>
            {delivery.distance_km && (
              <Badge variant="secondary">{delivery.distance_km} km</Badge>
            )}
          </div>
        </Card>

        {/* Delivery Details */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {/* Pickup Location */}
          <Card className="p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-blue-500" />
              Pickup Location
            </h3>
            <p className="font-medium">{delivery.pickup_location?.name}</p>
            <p className="text-sm text-muted-foreground">
              {delivery.pickup_location?.address}
            </p>
          </Card>

          {/* Dropoff Location */}
          <Card className="p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-emerald-500" />
              Delivery Address
            </h3>
            <p className="font-medium">{delivery.dropoff_location?.address}</p>
            <p className="text-sm text-muted-foreground">
              {delivery.dropoff_location?.city}, {delivery.dropoff_location?.state}
            </p>
          </Card>
        </div>

        {/* ETA */}
        {delivery.estimated_arrival && delivery.status !== 'delivered' && delivery.status !== 'failed' && (
          <Card className="p-4 mb-6 bg-emerald-50 border-emerald-200">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-emerald-600" />
              <div>
                <p className="text-sm text-emerald-700">Estimated Arrival</p>
                <p className="font-bold text-emerald-800">
                  {new Date(delivery.estimated_arrival).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Driver Info */}
        {delivery.driver && (
          <Card className="p-4 mb-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <User className="h-4 w-4" />
              Driver Information
            </h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{delivery.driver.name}</p>
                {delivery.driver.vehicle && (
                  <p className="text-sm text-muted-foreground">{delivery.driver.vehicle}</p>
                )}
              </div>
              {delivery.driver.phone && (
                <Button variant="outline" size="sm" asChild>
                  <a href={`tel:${delivery.driver.phone}`}>
                    <Phone className="h-4 w-4 mr-1" />
                    Call
                  </a>
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Proof of Delivery */}
        {delivery.proof_of_delivery && (
          <Card className="p-4 mb-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              Proof of Delivery
            </h3>
            <img 
              src={delivery.proof_of_delivery} 
              alt="Proof of delivery"
              className="w-full rounded-lg"
            />
          </Card>
        )}

        {/* Mock Notice */}
        {delivery.is_mock && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-center">
            <p className="text-sm text-amber-700">
              <AlertCircle className="h-4 w-4 inline mr-1" />
              This is a demo delivery. Configure your Burq API key for live tracking.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryTrackingPage;
